import { InjectionToken, ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import {
  MESSAGE_FORMAT_CONFIG,
  TranslateMessageFormatCompiler,
} from 'ngx-translate-messageformat-compiler';
import {
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withXhr,
} from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { deepmerge } from 'deepmerge-ts';
import { AppLanguage } from '@tnq/shared/types';
import { TitleStrategy } from '@angular/router';
import { TranslatedPageTitleStrategy } from './translated-title.strategy';

export const PATH = new InjectionToken<string>('path');

export class MultiTranslateHttpLoaderComponent implements TranslateLoader {
  constructor(
    private _handler: HttpBackend,
    private _resourcesPrefix: string[],
  ) {}

  public getTranslation(lang: string): Observable<unknown> {
    const requests: Observable<object>[] = this._resourcesPrefix.map(
      (resource: string) => {
        return new HttpClient(this._handler).get(`${resource}${lang}.json`);
      },
    );

    return forkJoin(requests).pipe(map((response) => deepmerge(...response)));
  }
}

@NgModule({
  imports: [
    CommonModule,
    TranslateModule.forRoot({
      useDefaultLang: true,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend, PATH],
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler,
      },
    }),
  ],
  exports: [TranslateModule],
  providers: [
    provideHttpClient(withXhr()),
    { provide: TitleStrategy, useClass: TranslatedPageTitleStrategy },
  ],
})
export class TranslationConfigModule {
  public static register({
    path,
    locales,
  }: {
    path: string;
    locales: AppLanguage[];
  }): ModuleWithProviders<TranslationConfigModule> {
    return {
      ngModule: TranslationConfigModule,
      providers: [
        { provide: PATH, useValue: path },
        {
          provide: MESSAGE_FORMAT_CONFIG,
          useValue: { locales },
        },
      ],
    };
  }
}

function HttpLoaderFactory(_httpBackend: HttpBackend, path: string) {
  return new MultiTranslateHttpLoaderComponent(_httpBackend, [
    path,
    'assets/i18n/shared-',
  ]);
}
